/**
 * AplombApiClient TS — pair du client Kotlin (shared/api/AplombApiClient.kt).
 *
 * Convention : payloads keys snake_case (aligné backend Rust axum + OpenAPI),
 * pas de transformation camelCase côté client → réduit surface bugs.
 *
 * Idempotency-Key : généré par appel via crypto.randomUUID() (Web Crypto,
 * supporté SSR Node 20+ et tous browsers modernes).
 */

export interface SubmitProApplicationRequest {
  email: string;
  business_name: string;
  legal_name: string;
  neq: string;
  rbq_number: string;
  cmmtq_number: string;
  postal_code_prefix: string;
  city: string;
  phone?: string;
}

export interface SubmitProApplicationResponse {
  application_id: string;
  pro_id: string;
  status: string;
  message: string;
}

export interface SubmitUrgencyRequest {
  client_email: string;
  postal_code_prefix: string;
  category: "urgency-plumbing" | "urgency-drainage" | "urgency-water-heater";
  description: string;
  photos?: string[];
  phone: string;
}

export interface SubmitUrgencyResponse {
  request_id: string;
  status: string;
  eta_minutes: number;
  proposals_expected: number;
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
}

export type ApiResult<T> =
  | { ok: true; value: T }
  | { ok: false; status: number; title: string; detail: string };

interface ClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

export class AplombApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;

  constructor(options: ClientOptions = {}) {
    this.baseUrl =
      options.baseUrl ??
      (typeof process !== "undefined" && process.env?.APLOMB_API_BASE) ??
      "https://api.aplomb.jescy.dev";
    this.fetchImpl = options.fetchImpl ?? fetch;
    this.timeoutMs = options.timeoutMs ?? 30_000;
  }

  submitProApplication(
    body: SubmitProApplicationRequest,
  ): Promise<ApiResult<SubmitProApplicationResponse>> {
    return this.postJson("/v1/pros/applications", body);
  }

  submitUrgencyRequest(
    body: SubmitUrgencyRequest,
  ): Promise<ApiResult<SubmitUrgencyResponse>> {
    return this.postJson("/v1/requests/urgency", body);
  }

  private async postJson<T>(path: string, body: unknown): Promise<ApiResult<T>> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Idempotency-Key": genUuid(),
          "X-Aplomb-Client": "aplomb-web-solid/0.1",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (response.ok) {
        const value = (await response.json()) as T;
        return { ok: true, value };
      }

      const problem = (await response.json().catch(() => null)) as ProblemDetails | null;
      return {
        ok: false,
        status: response.status,
        title: problem?.title ?? response.statusText,
        detail: problem?.detail ?? `HTTP ${response.status} sans détail`,
      };
    } catch (err) {
      clearTimeout(timeout);
      return {
        ok: false,
        status: 0,
        title: "Erreur réseau",
        detail: err instanceof Error ? err.message : "inconnue",
      };
    }
  }
}

function genUuid(): string {
  // crypto.randomUUID est standard Web Crypto (support SSR Node >= 19).
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback RFC 4122 v4 minimal.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
